import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { GitBranchIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import type { ImagineUIDataParts } from "@imagine/ai-service/shared-types";
import { cn } from "@/lib/utils";
import { imagineStore } from "@/stores/imagine.store";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";

export function Version({
  messageId,
  data,
  canRestore,
  isLatestVersion,
  className,
}: {
  messageId: string;
  data: ImagineUIDataParts["version"];
  canRestore: boolean;
  isLatestVersion: boolean;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(!isLatestVersion);
  const { shortSha, commitMessage, timestamp } = data;
  const [displayRestoreDialog, setDisplayRestoreDialog] = useState(false);

  const formattedTimestamp = useMemo(() => {
    const now = new Date();

    // If within the past hour, show "X minutes ago"
    if (now.getTime() - new Date(timestamp).getTime() < 1000 * 60 * 60) {
      const minutesAgo = Math.floor(
        (now.getTime() - new Date(timestamp).getTime()) / (1000 * 60),
      );
      if (minutesAgo < 1) {
        return "a few moments ago";
      }
      return `${minutesAgo} minute${minutesAgo > 1 ? "s" : ""} ago`;
    }

    return new Date(timestamp).toLocaleString("en-US", {
      year:
        now.getFullYear() === new Date(timestamp).getFullYear()
          ? undefined
          : "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [timestamp]);

  const handleRestore = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setDisplayRestoreDialog(true);
  };

  return (
    <div
      className={cn(
        "text-[0.7rem] w-md max-w-full border border-border/60 bg-card rounded-md shadow-xs relative",
        expanded && "border-b-0",
        className,
      )}
    >
      <div
        className="flex items-center px-2.5 h-8.5 gap-2 bg-primary/5 rounded-md border-b border-border/60 hover:bg-muted/70 transition-colors cursor-pointer [box-shadow:0_2px_0_0_rgba(255,255,255,0.16)_inset] dark:[box-shadow:0_1px_0_0_rgba(255,255,255,0.08)_inset]"
        onClick={() => setExpanded(!expanded)}
      >
        <GitBranchIcon className="size-3 text-muted-foreground/70" />
        <div className="font-medium flex-1 text-primary/80 text-xs">
          Version {shortSha}
        </div>
        <div className="text-muted-foreground/70">{formattedTimestamp}</div>
        {canRestore && (
          <div
            className="text-[0.65rem] border border-muted-foreground/10 transition-colors text-muted-foreground/70 font-medium px-2 py-0.5 rounded-sm hover:bg-muted-foreground/5 cursor-pointer"
            onClick={handleRestore}
          >
            Restore
          </div>
        )}
      </div>
      <div
        className={cn("p-2 text-foreground/60 font-mono", expanded && "hidden")}
      >
        {commitMessage}
      </div>
      <RestoreDialog
        open={displayRestoreDialog}
        onClose={() => setDisplayRestoreDialog(false)}
        data={data}
        messageId={messageId}
      />
    </div>
  );
}

function RestoreDialog({
  open,
  onClose,
  data,
  messageId,
}: {
  open: boolean;
  onClose: () => void;
  data: ImagineUIDataParts["version"];
  messageId: string;
}) {
  const projectId = imagineStore.use.projectId();
  const {
    mutate: restoreVersion,
    isPending,
    error,
    reset,
  } = useMutation({
    mutationFn: async () => {
      const response = await aiServiceFetch(
        `/api/projects/${projectId}/restore`,
        {
          method: "POST",
          body: JSON.stringify({ messageId, commitSha: data.sha }),
        },
      );

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      return response.json();
    },
    onSuccess: () => {
      onClose();
      window.location.reload();
    },
  });

  const handleRestore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    restoreVersion();
  };

  useEffect(() => {
    // Clear errors from the mutation
    if (!open) {
      reset();
    }
  }, [open, reset]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isPending && !isOpen && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-base">Are you sure?</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1 text-foreground/70">
          <p>
            You are about to restore to version{" "}
            <span className="font-semibold font-mono">{data.shortSha}</span>.
          </p>
          <p>This operation cannot be undone.</p>
          {error && <p className="text-destructive mt-4">{error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={isPending}
            variant="destructive"
            onClick={handleRestore}
          >
            Restore
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
